import { makeAutoObservable } from "mobx";
import { PROFILE_CAPABILITIES } from "../../../models/ProfileCapabilities";
import dashboardSettingsStore from "../../../stores/dashboard-settings-store";
import { IChangeable } from "../../../stores/types";
import { CONDITION_OPERATORS, IConditionByLevelPreset, IConditionError, IConditionFieldPreset, IPolicyCondition } from "../policy-types";
import { ICompareValueStore, createCompareValueStore } from "./compare-value/compare-value-factory";
import PolicyConditionGroupStore from "./policy-condition-group-store";

interface IStashedCompareValueStoresMap {
    [field: string]: {
        store?: ICompareValueStore<any>;
        operator?: CONDITION_OPERATORS;
    };
}
export interface IPolicyConditionChanges extends Partial<Omit<IPolicyCondition, 'operator'>> {
    operator?: CONDITION_OPERATORS|null;
};

class ConditionBuilderStore implements IChangeable<IPolicyCondition> {
    private _conditionGroupStore: PolicyConditionGroupStore = {} as PolicyConditionGroupStore;
    private _model: IPolicyCondition = {} as IPolicyCondition;
    private _changes: IPolicyConditionChanges = {};
    private _uncommittedChanges: IPolicyConditionChanges = {};
    private _compareValueStore?: ICompareValueStore<any>;
    private _stashedCompareValueStoreMap: IStashedCompareValueStoresMap = {};
    private _isEditMode: boolean = false;
    private _error: IConditionError|undefined = undefined;

    constructor(conditionGroupStore: PolicyConditionGroupStore, model: IPolicyCondition) {
        makeAutoObservable(this);
        this._conditionGroupStore = conditionGroupStore;
        this._model = model;
        this._setCompareValueModel();
    }

    private _setCompareValueModel() {
        if (this.field) {
            this._compareValueStore = createCompareValueStore(
                this._fieldPreset,
                this._conditionGroupStore.policy.returnReasonsPreset.reasonsList,
                this._model.compare_value
            );
        }
    }

    public setChanges(changes: IPolicyConditionChanges): void {
        delete changes.id;

        this._changes = changes;
        if (this.field) {
            this._compareValueStore = createCompareValueStore(this._fieldPreset, this._conditionGroupStore.policy.returnReasonsPreset.reasonsList);
            this._compareValueStore.setChanges(this._changes.compare_value);
        }
    }

    public get id(): number { return this._model.id; }
    public get operator(): CONDITION_OPERATORS|undefined { 
        /** special case, allowing operator to be null when switching field */
        if (this._uncommittedChanges.operator === null) {
            return undefined;
        }
        return this._uncommittedChanges.operator ?? this._changes.operator ?? this._model.operator; 
    }
    public get field(): string { return this._uncommittedChanges.field ?? this._changes.field ?? this._model.field; }
    
    public get conditionGroupStore() { return this._conditionGroupStore; }

    public get isEditMode() { return this._isEditMode; }

    private get _preset(): IConditionByLevelPreset { 
        return this.conditionGroupStore.policy.conditionPreset.getPresetByLevel(this.conditionGroupStore.level); 
    }
    private get _fieldPreset(): IConditionFieldPreset { return this._preset.conditions[this.field]; }

    public getConditionOperatorList() { return this._preset.group.operators; }
    public getConditionFieldList() { 
        const resolution = this.conditionGroupStore.policy.resolution;
        const conditionFieldOptions = Object.values(this._preset.conditions).map(c => ({ label: c.label, value: c.value }));
        const { supportedEligibilityConditions } = dashboardSettingsStore.resolveCapability(
            PROFILE_CAPABILITIES.RETURN_POLICY_RULE
        );
        const allowedFields = supportedEligibilityConditions?.length
            ? new Set(supportedEligibilityConditions)
            : null;

        return conditionFieldOptions.filter(f => {
            if (allowedFields && !allowedFields.has(f.value)) {
                return false;
            }

            if (!resolution) {
                return true;
            }

            const limitations = this._preset.limitations_by_resolution_type[resolution.resolution_type];

            // if no limitations found, or no rules for fields - return all fields
            if (!limitations || (!limitations.allowed_conditions && !limitations.not_allowed_conditions)) {
                return true;
            }

            if (limitations.allowed_conditions) {
                return limitations.allowed_conditions.includes(f.value as string);
            }

            return !limitations.not_allowed_conditions.includes(f.value as string);
        });
    }

    public get compareValueStore() { return this._compareValueStore; }
    public get operators() { return this._fieldPreset?.operators ?? []; }
    public get compareValueType() { return this._fieldPreset?.compare_value_type; }
    public get text() { return this._fieldPreset?.text ?? this.field; }
    public get tooltip() { return this._fieldPreset?.tooltip; }
    public get placeholder() { return this._fieldPreset?.input_placeholder; }
    public get deleted() { return this._changes._deleted ?? false }
    public get error() { return this._error; }

    public setOperator(value: CONDITION_OPERATORS) {
        this._uncommittedChanges.operator = value;
        this._compareValueStore?.onOperatorChanged(value);
    }

    public setField(value: string) {
        if (this.field === value) {
            return;
        }

        /** stash previos compare value store and operator before making changes */
        if (this._compareValueStore) {
            this._stashedCompareValueStoreMap[this.field] = {
                store: this._compareValueStore,
                operator: this.operator,
            }
        }

        /** attempt to restore previos compare value store and operator if we already have them stashed */
        this._compareValueStore = this._stashedCompareValueStoreMap[value]?.store;
        this._uncommittedChanges.operator = this._stashedCompareValueStoreMap[value]?.operator ?? null;

        this._uncommittedChanges.field = value;
        if (!this._compareValueStore) {
            this._compareValueStore = createCompareValueStore(this._fieldPreset, this._conditionGroupStore.policy.returnReasonsPreset.reasonsList);
        }
    }

    public setDeleted(v: boolean) {
        this._changes._deleted = v;
        if (v) {
            this._uncommittedChanges = {};
            this._isEditMode = false;
        }
    }

    public deleteIfNotAllowed() {
        const fields = this.getConditionFieldList();
        if (!fields.find(f => f.value === this.field)) {
            this.setDeleted(true);
        }
    }

    public setError(v: IConditionError|undefined) {
        this._error = v;
    }

    public setIsEditMode(v: boolean) { 
        this._isEditMode = v; 
    }

    private _clearUnneededChanges() {
        Object.keys(this._changes).forEach(key => {
            const theKey = key as keyof IPolicyCondition;
            if (this._changes[theKey] === this._model?.[theKey]) {
                delete this._changes[theKey];
            }
        });
    }

    commitChanges(): void {
        this._changes = {...this._changes, ...this._uncommittedChanges};
        if (this._compareValueStore) {
            this._compareValueStore.commitChanges();
        }
        this._stashedCompareValueStoreMap = {};

        /** Need to discard uncommited changes after merging them with _changes */
        this.discardUncommittedChanges();
        this._clearUnneededChanges();
    }
 
    discardUncommittedChanges(): void {
        this._uncommittedChanges = {};

        /** if while editing condition we changes the field - restore */
        if (this._stashedCompareValueStoreMap[this.field]) {
            this._compareValueStore = this._stashedCompareValueStoreMap[this.field].store;
        }
        this._stashedCompareValueStoreMap = {};

        if (this._compareValueStore) {
            this._compareValueStore.discardUncommittedChanges();
        }
    }

    hasChanges(): boolean {
        return (this._compareValueStore?.hasChanges() ?? false) || !!Object.keys(this._changes).length;
    }

    discardChanges(): void {
        this._changes = {};
        this._stashedCompareValueStoreMap = {};
        this._uncommittedChanges = {};
        this._setCompareValueModel();
    }

    /** condition's state when it is 
     *  - not in edit mode 
     *  - created and deleted on client
     *  - has no information exept action properties (_has_chanegs, _deleted) */
    isGhost(): boolean {
        const _debLog = (isGhost:boolean) => {
            console.log(this.getUpdatedModel(), `isGhost:${isGhost.toString().toUpperCase()}`, );
        };

        if (this.isEditMode) {
            _debLog(false);
            return false;
        }

        /** when created and deleted on client */
        const isNewAndDeleted = !(this._model ?? {}).id && this.deleted;
        if (isNewAndDeleted) {
            _debLog(true);
            return true;
        }

        /** when has no information exept action properties (_has_chanegs, _deleted) */
        const updatedModel = this.getUpdatedModel() ?? {} as IPolicyConditionChanges;
        const hasInfo = Object.keys(updatedModel).reduce<boolean>((hasLegitProperty, key) => {
            if (hasLegitProperty || ['_has_changes', '_deleted'].includes(key)) {
                return hasLegitProperty;
            }
            return updatedModel[key as keyof IPolicyCondition] !== undefined;
        }, false);
        
        _debLog(!hasInfo);
        return !hasInfo;
    }

    getUpdatedModel(): IPolicyCondition|undefined {
        const updatedModel = { 
            ...this._model, ...this._changes,
            compare_value: this._compareValueStore?.getUpdatedModel() as string,
            _has_changes: this.hasChanges(), 
        };
        
        return updatedModel as IPolicyCondition;
    }

    /** should be used for validations */
    getUncommittedChanges(): IPolicyConditionChanges {
        return {
            ...this._model,
            ...this._changes,
            ...this._uncommittedChanges,
            compare_value: this._compareValueStore?.getUncommittedChanges()
        };
    }
}

export default ConditionBuilderStore;
